import { ErrorHandler, Injectable, NgZone, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { HttpService } from '../services/http.service';
import { ToastService } from '../services/toast.service';
import { TranslationService } from '../services/translation.service';
import { AuthorizationService } from '../services/authorization.service';
import { AuthService } from '@auth0/auth0-angular';
import { SpinnerService } from '../services/spinner.service';

export type Error = {
  Message: string;
  StackTrace?: string;
  FileName?: string;
  Component?: string;
  LineNumber?: number;
};

@Injectable()
export default class GlobalErrorHandler implements ErrorHandler {
  error: Error = { Message: '' };
  private readonly errorUrl = `${environment.baseApiUrl}/Error/Log`;
  private isRefreshing = false;

  @Inject(DOCUMENT) private doc!: Document;

  constructor(
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private translationService: TranslationService,
    private zone: NgZone,
    private httpService: HttpService,
    private authorizationService: AuthorizationService,
    private auth0: AuthService
  ) {}

  handleError = (error: any): void => {
    if (!(error instanceof HttpErrorResponse) && error.rejection) {
      error = error.rejection;
    }

    this.zone.run(() => {
      this.spinnerService.hideSpinner();

      const chunkFailedMessage = /Loading chunk [\d]+ failed/;
      if (chunkFailedMessage.test(error?.message || '')) {
        window.location.reload();
        return;
      }

      const errorMessage =
        this.translationService.translate('error_C3004') +
        ' ' +
        this.translationService.translate('error_contact_support');

      this.toastService.error(errorMessage);

      // Build a clean log message
      const formattedLog = this.formatError(error);

      // Log to console (dev only)
      if (!environment.production) {
        console.error(formattedLog);
      }

      // Send to backend
      this.httpService.post(this.errorUrl, this.error).catch(() => {});

      if (error?.message === 'Login required' && !this.isRefreshing) {
        this.isRefreshing = true;
        // this.auth0.logout({ returnTo: location.origin });
      }
    });
  };

  private formatError(error: any): string {
    const stackLines = (error?.stack || '').split('\n');
    let locationInfo = '';

    // Try to extract file name and line number
    for (const line of stackLines) {
      const match = line.match(/(http.*):(\d+):(\d+)/);
      if (match) {
        const [_, filePath, lineNum] = match;
        const fileName = filePath.split('/').pop();
        this.error.FileName = fileName;
        this.error.LineNumber = parseInt(lineNum);
        locationInfo = `📄 File: ${fileName}  📌 Line: ${lineNum}`;
        break;
      }
    }

    // Try to guess component from stack
    const componentLine = stackLines.find((l: any) => l.includes('Component'));
    if (componentLine) {
      const compMatch = componentLine.match(/at (\w+Component)/);
      if (compMatch) {
        this.error.Component = compMatch[1];
      }
    }

    this.error.Message = error?.message || JSON.stringify(error);
    this.error.StackTrace = error?.stack || '';

    return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🧱 Error Message: ${this.error.Message}
🧩 Component: ${this.error.Component || 'Unknown'}
${locationInfo}
🪵 Stack Trace:
${this.error.StackTrace}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();
  }
}
